import { Link } from "react-router-dom";
import { ArrowRight, SearchCheck, Wrench, CircleDot, Ruler, Cog, ClipboardCheck, FileCheck2, KeyRound, Sparkles, Route, Check } from "lucide-react";

const ICONS = { SearchCheck, Wrench, CircleDot, Ruler, Cog, ClipboardCheck, FileCheck2, KeyRound, Sparkles, Route };
import { Reveal, SectionLabel } from "@/components/site/Reveal";
import { STANDARD_ITEMS } from "@/lib/content";

const SAMPLE = [
  ["Service due", "Completed"], ["Front brakes", "9mm"], ["Rear brakes", "8mm"], ["Front tyres", "7mm"],
  ["Rear tyres", "7mm"], ["4WD system", "Checked"], ["MOT", "Resolved"], ["Keys", "Two"],
];

export default function StandardSection() {
  return (
    <section className="on-dark grain relative overflow-hidden bg-forest text-paper" data-testid="standard-section">
      <div className="relative z-[2] mx-auto grid max-w-[1440px] gap-16 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-36">
        <div className="lg:col-span-6">
          <Reveal>
            <SectionLabel index="04" light>The details matter</SectionLabel>
            <h2 className="mt-6 font-serif text-5xl leading-[1] sm:text-6xl lg:text-7xl">The Cirencester<br /><em className="italic text-stone">Standard</em></h2>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-paper/90 sm:text-lg">
              Every vehicle is inspected, checked and prepared before it reaches the forecourt. Rather than hiding behind generic ‘multi-point inspection’ claims, we show customers what has actually been checked and completed.
            </p>
          </Reveal>
          <ul className="mt-12 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
            {STANDARD_ITEMS.map(([label, icon], i) => {
              const Icon = ICONS[icon] || Check;
              return (
                <Reveal as="li" key={label} delay={i * 0.04} y={14} className="flex items-center gap-4 border-b border-paper/15 py-4">
                  <Icon className="h-5 w-5 shrink-0 text-stone" strokeWidth={1.25} />
                  <span className="text-[0.95rem]">{label}</span>
                </Reveal>
              );
            })}
          </ul>
          <Link to="/the-cirencester-standard" className="btn btn-light mt-12" data-testid="standard-cta">See our preparation standard <ArrowRight className="arrow h-4 w-4" /></Link>
        </div>
        <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.15}>
          <div className="relative">
            <img src="/img/workshop.webp" alt="Technician measuring a brake disc in our timber-beamed preparation workshop" loading="lazy" className="aspect-[4/5] w-full object-cover" />
            <div className="relative mx-3 -mt-14 bg-paper p-5 text-charcoal shadow-[0_30px_60px_-30px_rgba(0,0,0,.5)] sm:absolute sm:-bottom-10 sm:left-6 sm:right-6 sm:mx-0 sm:mt-0 sm:p-6 lg:-left-12 lg:right-10" data-testid="standard-sample-report">
              <div className="flex items-center justify-between border-b border-stone pb-3">
                <p className="font-serif text-xl text-forest">Preparation report</p>
                <p className="eyebrow !text-[0.6rem] text-charcoal/70">Sample</p>
              </div>
              <dl className="mt-1 grid grid-cols-1 gap-x-6 text-sm sm:mt-2 sm:grid-cols-2">
                {SAMPLE.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-4 border-b border-stone/60 py-2.5 last:border-b-0 sm:py-2 sm:[&:nth-last-child(2)]:border-b-0"><dt className="text-charcoal/75">{k}</dt><dd className="whitespace-nowrap text-right font-semibold text-forest">{v}</dd></div>
                ))}
              </dl>
            </div>
          </div>
        </Reveal>
      </div>
      <div className="hidden sm:block sm:h-10 lg:h-0" />
    </section>
  );
}
